import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { GridInformation } from 'vnetwork-ui'
import {
    GetAllTenderDocuments,
    GetTenderByID,
    GetTenderDocument,
    GetTenderDocuments,
    extractFilenameFromHeader,
} from '../../services'
import TenderDocuments from './TenderDocuments'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Button } from 'vnetwork-ui'
import { formatDateWithFormatter } from '@/utils/function'
import { BID_FORM_TYPES, FIELD_TYPES } from '../List/tender-list-config'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { downloadBlob } from '@/modules/Drive/utils/ThumbnailGenerator'
import { useEffect } from 'react'
import toast from 'react-hot-toast'

const Detail = () => {
    const { tenderId } = useParams<{ tenderId: string }>()
    const { t } = useTranslation('tenders')
    const navigate = useNavigate()

    const { data: tender, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TENDER_DETAIL, tenderId],
        queryFn: async () => {
            const result = await GetTenderByID(Number(tenderId) || 0)
            return result
        },
    })

    const { data: documents } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TENDER_DOCUMENTS, tender?.result?.notify_id],
        queryFn: async () => {
            if (!tender?.result?.notify_id) return { result: [] }
            const res = await GetTenderDocuments({
                notify_id: tender.result.notify_id,
            })
            return res
        },
        enabled: !!tender?.result?.notify_id,
    })

    const {
        mutateAsync: getTenderDocument,
        isPending: isDownloading,
        isError: isDownloadError,
        error: downloadError,
    } = useMutationWithHandler(
        {
            mutationFn: (file_id: string) => GetTenderDocument(file_id),
        },
        {
            onSuccess: () => {
                toast.dismiss()
                toast.success(t('download_document_success'))
            },
            showErrorToast: false,
            reactQueryKeys: [],
        }
    )

    const {
        mutateAsync: getAllTenderDocument,
        isPending: isDownloadingAll,
        isError: isDownloadAllError,
        error: downloadAllError,
    } = useMutationWithHandler(
        {
            mutationFn: (notify_id: string) => GetAllTenderDocuments(notify_id),
        },
        {
            onSuccess: () => {
                toast.dismiss()
                toast.success(t('download_document_success'))
            },
            showErrorToast: false,
            reactQueryKeys: [],
        }
    )

    // Show loading toast when downloading
    useEffect(() => {
        if (isDownloading || isDownloadingAll) {
            toast.loading(t('downloading_document'))
        }
    }, [isDownloading, isDownloadingAll, t])

    // Show error toast when download fails
    useEffect(() => {
        if (isDownloadError || isDownloadAllError) {
            console.log('[DEBUG]: downloadError: ', downloadError)
            toast.dismiss()
            toast.error(t('download_document_error'))
        }
    }, [
        isDownloadError,
        downloadError,
        isDownloadAllError,
        downloadAllError,
        t,
    ])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const tenderData = tender?.result
    const documentsList = documents?.result || []

    const handleViewBidResult = () => {
        if (tenderId) {
            navigate(
                generatePathDatacentral(paths.tenderBidResult, {
                    tenderID: tenderId,
                })
            )
        }
    }

    const handleDownloadDocument = async (file_id?: string) => {
        const idToDownload =
            file_id || documentsList?.[0]?.documents?.[0]?.file_id

        if (!idToDownload) {
            toast.error(t('download_document_error'))
            return
        }
        try {
            const response = await getTenderDocument(idToDownload)
            if (!response) {
                console.error('Download document failed: response is undefined')
                toast.error(t('download_document_error'))
                return
            }
            downloadBlob(
                response.data,
                extractFilenameFromHeader(
                    response.headers['content-disposition']
                )
            )
        } catch (error) {
            console.error('Download failed:', error)
        }
    }

    const handleDownloadAllDocument = async () => {
        const notify_id = tender?.result?.notify_id
        if (!notify_id) {
            toast.error(t('download_document_error'))
            return
        }
        try {
            const response = await getAllTenderDocument(notify_id)
            if (!response) {
                console.error(
                    'Download all documents failed: response is undefined'
                )
                toast.error(t('download_document_error'))
                return
            }
            downloadBlob(
                response.data,
                extractFilenameFromHeader(
                    response.headers['content-disposition']
                )
            )
        } catch (error) {
            console.error('Download failed:', error)
        }
    }

    return (
        <div className="mb-4 flex flex-col gap-2">
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.tenders,
                        title: t('title'),
                    },
                    {
                        path: generatePathDatacentral(paths.tenderDetail, {
                            tenderId: tenderId || '',
                        }),
                        title: `${tenderData?.code || tenderId} - ${tenderData?.name || ''}`,
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: t('code'),
                        value: tenderData?.code || '-',
                    },
                    {
                        label: t('name'),
                        value: tenderData?.name || '-',
                    },
                    {
                        label: t('investor'),
                        value: tenderData?.investor || '-',
                    },
                    {
                        label: t('field'),
                        value:
                            FIELD_TYPES[
                                tenderData?.field as keyof typeof FIELD_TYPES
                            ] ||
                            tenderData?.field ||
                            '-',
                    },
                    {
                        label: t('location'),
                        value: tenderData?.location || '-',
                    },
                    {
                        label: t('publish_date'),
                        value: tenderData?.publish_date
                            ? formatDateWithFormatter(
                                  tenderData.publish_date,
                                  'HH:mm DD/MM/YYYY'
                              )
                            : '-',
                    },
                    {
                        label: t('bid_time'),
                        value: tenderData?.bid_time
                            ? formatDateWithFormatter(
                                  tenderData.bid_time,
                                  'HH:mm DD/MM/YYYY'
                              )
                            : '-',
                    },
                    {
                        label: t('bid_form'),
                        value:
                            BID_FORM_TYPES[
                                tenderData?.bid_form as keyof typeof BID_FORM_TYPES
                            ] ||
                            tenderData?.bid_form ||
                            '-',
                    },
                    {
                        label: t('view_details'),
                        value: tenderData?.detail_url ? (
                            <a
                                href={tenderData.detail_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                            >
                                {t('link_to_detail')}
                            </a>
                        ) : (
                            '-'
                        ),
                    },
                ]}
                head={{
                    title: t('detailed-information'),
                }}
                footer={{
                    components: [
                        <Button
                            key="bid-result"
                            variant="contained"
                            onClick={handleViewBidResult}
                        >
                            {t('view_bid_result')}
                        </Button>,
                    ],
                }}
            />

            <TenderDocuments
                documents={documentsList}
                onDownload={(file_id) => handleDownloadDocument(file_id)}
                onDownloadAll={handleDownloadAllDocument}
                isDownloading={isDownloading}
            />
        </div>
    )
}

export default Detail
